import type { PatternIconsDto, PatternPicturesDto } from '@nsb/core';
import { toneLook } from './look.ts';

/**
 * A pattern's painted picture (spec "Pattern Mapping" §4.7), taken out of the components that draw it so it is tested
 * on its own: a job's pictures by the pattern's number, and what stands where a pattern's picture goes, its picture or,
 * until it is painted, its scale's tone. No placeholder is ever drawn.
 */

/** A job's pictures by the pattern's number; one sent twice keeps the first. None until they come. */
export function picturesByNumber(dto: PatternPicturesDto | undefined): Map<number, string> {
  const out = new Map<number, string>();
  for (const p of dto?.pictures ?? []) if (!out.has(p.n)) out.set(p.n, p.url);
  return out;
}

export type PictureLook = { kind: 'picture'; src: string } | { kind: 'tone'; fill: string; edge: string };

/**
 * What stands where a pattern's picture goes: its picture once painted, its scale's tone until then, and where the
 * picture it was given could not be loaded (`broken`: its file gone, or the network down).
 */
export function pictureLook(pictures: ReadonlyMap<number, string>, n: number, broken: string | null = null): PictureLook {
  const src = pictures.get(n);
  return src && src !== broken ? { kind: 'picture', src } : { kind: 'tone', ...toneLook(n) };
}

/** What Paint does, in words: the ones still missing, trying again those that failed; one that has a picture is never painted again. */
export function paintWords(d: Pick<PatternIconsDto, 'missing' | 'failed'>): string {
  return `Paint the ${d.missing} still missing${d.failed ? ', trying again those that failed' : ''}`;
}

/** The picture in a circle on the map: its painted picture, or null (drawn as a circle) until it is painted and where it would not load. */
export function circlePicture(pictures: ReadonlyMap<number, string>, n: number, broken: ReadonlySet<string>): string | null {
  const src = pictures.get(n);
  return src && !broken.has(src) ? src : null;
}
